import { h, Fragment } from 'preact';
import { useMemo, useCallback, useState } from 'preact/hooks';
import clsx from 'clsx';
import { withCartContext } from './cart-context';
import Image from '../utils/image';
import { formatMoney, isOnSale, elevarTracking } from '../utils/helpers';
import PersonalizeIcon from '../utils/icons/personalize.svg';
import TrashIcon from '../utils/icons/trash.svg';
import CartItemCustomSelections from './cart-item-custom-selections';
import * as analytics from '../utils/analytics';

const CartItem = ({ cart, line, isCartPage, data }) => {
  const [lineQuantity, setLineQuantity] = useState(line.quantity);
  const lineVariants = line.merchandise.product.variants.edges;

  const currentVariant = lineVariants.find(
    (el) => el.node.id === line.merchandise.id,
  );
  const isEnvelope = line.attributes.some(
    (attr) => attr.key === 'envelope' && attr.value === 'true',
  );
  const isSheath = line.attributes.some(
    (attr) => attr.key === 'sheath' && attr.value === 'true',
  );
  const isService = line.attributes.some(
    (attr) => attr.key === 'request_type' && attr.value === 'Service',
  );

  const summ = {
    amount:
      (line.customKnife
        ? line.knifePrice.priceV2.amount
        : currentVariant.node.priceV2.amount) * line.quantity,
  };

  const adjustQuantity = useCallback(
    (quantity) => {
      cart.update(
        line.customKnife
          ? [line, ...line.parts].map((currentLine) => ({
              id: currentLine.id,
              quantity:
                // double quantity for parts with quantity greater than the knife
                // because this means there were two of the same part added
                currentLine.quantity > line.quantity ? quantity * 2 : quantity,
              attributes: currentLine.attributes,
            }))
          : {
              id: line.id,
              quantity,
              attributes: line.attributes,
            },
        false,
        true,
      );

      // elevar tracking
      // compare incoming qty from qty adjust buttons to qty state and then track accordingly
      const products = [
        {
          id: line.merchandise.sku,
          name: line.merchandise.product.title,
          brand: line.merchandise.product.vendor,
          category: line.merchandise.product.productType,
          variant: line.merchandise.product.variants.edges[0].node.title,
          price:
            line.merchandise.product.variants.edges[0].node.priceV2?.amount,
          quantity: '1',
          position: '1',
          list: document.referrer,
          product_id: line.merchandise.product.id.split('/Product/')[1],
          variant_id:
            line.merchandise.product.variants.edges[0].node.id?.split(
              '/ProductVariant/',
            )[1],
          compare_at_price:
            line.merchandise.product.variants.edges[0].node.compareAtPrice ||
            '0.00',
          image: line.merchandise.product.variants.edges[0].node.image?.src,
          url: window.location.href,
        },
      ];

      // quantity comes from the buttons and lineQuantity is from state (i.e. the previous quantity)
      if (quantity > lineQuantity) {
        elevarTracking({
          customerId: data.customerId,
          products,
          event: 'dl_add_to_cart',
        });
      } else {
        elevarTracking({
          customerId: data.customerId,
          products,
          event: 'dl_remove_from_cart',
        });
      }

      setLineQuantity(quantity);
    },
    [line, cart],
  );

  const handleRemove = useCallback(() => {
    analytics.removeToCartTrigger(line);
    const isGwp = line.attributes.find((attr) => attr.key === '_is_gwp')?.value;
    if (isGwp) {
      const date = new Date();
      date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
      // eslint-disable-next-line max-len
      document.cookie = `disabled_gwp=1; expires=${date.toUTCString()}; path=/`;
    }

    if (currentVariant.node) {
      const { node } = currentVariant;

      const products = [
        {
          id: node.sku,
          name: node.title,
          brand: line.merchandise.product.vendor,
          category: line.merchandise.product.productType,
          variant: node.title,
          price: node.priceV2.amount,
          quantity: '1',
          position: '1',
          list: document.referrer,
          product_id: line.merchandise.product.id.split('/Product/')[1],
          variant_id: node.id.split('/ProductVariant/')[1],
          compare_at_price: node.compareAtPrice,
          image: node.image.src,
          url: window.location.href,
        },
      ];

      elevarTracking({
        customerId: data.customerId,
        products,
        event: 'dl_remove_from_cart',
      });
    }

    cart.remove(
      line.customKnife
        ? [line.id, ...line.parts.map((part) => part.id)]
        : line.id,
    );
  }, [line, cart]);

  const lineImage = useMemo(() => {
    const standardImage = line.horizontalImage
      ? line.horizontalImage.value
      : currentVariant.node.image?.src;

    if (line.customKnife) {
      const customImage = line.attributes.find(
        (attr) => attr.key === '_product_image_url',
      )?.value;

      return {
        url: customImage || standardImage,
        isCustom: !!customImage,
      };
    }

    return {
      url: standardImage,
      isCustom: false,
    };
  }, [
    currentVariant.node.image?.src,
    line.attributes,
    line.customKnife,
    line.horizontalImage,
  ]);

  const threeKitId = line.attributes.find(
    (attr) => attr.key === '_threekit_id',
  )?.value;

  const isPersonalized = line.attributes.find(
    (attr) => attr.key === '_personalization',
  )?.value;

  const isApparel = line.attributes.find(
    (attr) => attr.key === '_is_apparel',
  )?.value;

  const apparelSize = line.attributes.find(
    (attr) => attr.key === '_apparel_size',
  )?.value;

  const apparelColor = line.attributes.find(
    (attr) => attr.key === '_apparel_color',
  )?.value;

  const lineDescription = line.shortDescription
    ? line.shortDescription.value
    : line.merchandise.product.description;
  const linePrice = line.customKnife
    ? line?.knifePrice?.priceV2
    : currentVariant.node.priceV2;
  const lineCompareAtPrice = line.customKnife
    ? line?.knifePrice?.compareAtPriceV2
    : currentVariant.node.compareAtPrice;

  const hasPersonalizationTag = line.merchandise.product.tags.find(
    (tag) => tag === 'cstm:personalization',
  );

  const isGwpLink = line.attributes.find(
    (attr) => attr.key === '_gwp_link',
  )?.value;
  const gwpLinkThreshold = line.attributes.find(
    (attr) => attr.key === '_gwp_link_threshold',
  )?.value;

  let gwpLinkRemain =
    Number.parseFloat(gwpLinkThreshold) - cart.state.subtotal.amount;
  if (!cart.state.gwpLinkFree) {
    gwpLinkRemain += Number.parseFloat(linePrice.amount);
  }

  const isGwp =
    line.attributes.find((attr) => attr.key === '_is_gwp')?.value ||
    (isGwpLink && gwpLinkRemain <= 0);

  const lineItem = useMemo(
    () => (
      <div
        key={line.id}
        className={clsx(
          'grid grid-cols-12 py-5 border-grey-400 grow',
          isGwp ? 'order-0' : 'order-1 border-t',
          {
            'laptop:flex laptop:max-w-[87%] laptop:py-[25px]': isCartPage,
          },
        )}
      >
        <div
          className={clsx(
            isCartPage ? 'col-span-6 laptop:basis-[16%]' : 'col-span-5',
          )}
        >
          {lineImage.url && (
            <a
              href={
                isGwp ? '#' : `/products/${line.merchandise.product.handle}`
              }
              className={clsx({
                'pointer-events-none cursor-default': isGwp,
              })}
            >
              <Image
                src={lineImage.url}
                className={clsx(
                  'block object-contain laptop:max-w-[135px]',
                  isCartPage ? 'max-w-[155px]' : 'max-w-[98px]',
                )}
                alt={`${line.merchandise.product.title} image`}
                srcWidths={
                  !lineImage.isCustom &&
                  !lineImage.url?.includes('_') && {
                    sm: 132,
                    md: 132,
                    lg: 132,
                    xl: 132,
                    '2xl': 132,
                  }
                }
              />
            </a>
          )}
        </div>
        <div
          className={clsx(
            isCartPage
              ? 'col-span-6 mt-1 laptop:basis-[84%] laptop:flex laptop:ml-5 laptop:mt-0'
              : 'col-span-7 laptop:pl-4 ',
            isCartPage && isGwp ? 'justify-between' : '',
          )}
        >
          <div
            className={clsx({
              'laptop:basis-[34%] laptop:pr-[12%]': isCartPage,
            })}
          >
            {line.customKnife && line.knifeConfiguration?.length && (
              <p className="mb-1 uppercase font-b text-accent-1">
                {isPersonalized
                  ? window.TMW.Strings.cart.personalized
                  : window.TMW.Strings.cart.customized}
              </p>
            )}
            <a
              href={
                isGwp ? '#' : `/products/${line.merchandise.product.handle}`
              }
              className={clsx(
                'block mb-1',
                {
                  'pointer-events-none cursor-default': isGwp,
                },
                isCartPage ? 'heading-5 laptop:heading-6' : 'heading-6',
              )}
            >
              {line.merchandise.product.title}
            </a>
            <span
              className={clsx(
                'block font-m-12  mb-1',
                isGwp ? 'text-accent-1 uppercase' : 'text-primary-1',
                {
                  'laptop:hidden': isCartPage && !isGwp,
                },
              )}
            >
              {isGwp ? (
                'Free Gift'
              ) : (
                <>
                  {formatMoney(linePrice)}
                  {currentVariant.node.compareAtPrice &&
                    isOnSale(linePrice, lineCompareAtPrice) && (
                      <del class=" text-secondary-1 pl-0.5">
                        {formatMoney(lineCompareAtPrice)}
                      </del>
                    )}
                </>
              )}
            </span>
            {threeKitId && (
              <a
                aria-label="View Configured Product"
                href={`/products/${line.merchandise.product.handle}?tkcsid=${threeKitId}`}
                className={clsx(
                  'mt-2 py-1.5 px-2.5',
                  'font-r-12 text-left whitespace-nowrap',
                  'border-[1px] border-solid border-grey-400 rounded',
                  'hover:bg-grey-400 hover:cursor-pointer',
                  isCartPage ? 'mb-4' : 'mb-3',
                )}
              >
                BKC ID: {threeKitId}
              </a>
            )}
            {}
            {line.customKnife && line.knifeConfiguration?.length ? (
              <CartItemCustomSelections
                configuration={line.knifeConfiguration}
                isCartPage={isCartPage}
              />
            ) : (
              <>
                {lineDescription && (
                  <p
                    className={clsx(
                      'text-secondary-1 mb-3 overflow-hidden',
                      isCartPage
                        ? 'font-r-12 max-h-[46px]'
                        : 'font-r-10 max-h-[42px]',
                    )}
                  >
                    {lineDescription}
                  </p>
                )}
                {hasPersonalizationTag && (
                  <div className="flex flex-wrap items-center">
                    <PersonalizeIcon class="w-[22px] mr-1" />
                    <a
                      href={`/products/${line.merchandise.product.handle}?customizerOpen=true&cartUpdate=true`}
                      className="underline font-m-12 text-primary-1"
                    >
                      {window.TMW.Strings.cart.personalize}
                    </a>
                  </div>
                )}
              </>
            )}
            {isApparel && (
              <>
                {apparelColor && (
                  <p
                    className={clsx(
                      'text-secondary-1 mb-1 overflow-hidden',
                      isCartPage
                        ? 'font-r-12 max-h-[46px]'
                        : 'font-r-10 max-h-[42px]',
                    )}
                  >
                    <b
                      className={
                        isCartPage
                          ? 'font-m-12 max-h-[46px]'
                          : 'font-m-10 max-h-[42px]'
                      }
                    >
                      Color:
                    </b>{' '}
                    {apparelColor}
                  </p>
                )}
                {apparelSize && (
                  <p
                    className={clsx(
                      'text-secondary-1 mb-1 overflow-hidden',
                      isCartPage
                        ? 'font-r-12 max-h-[46px]'
                        : 'font-r-10 max-h-[42px]',
                    )}
                  >
                    <b
                      className={
                        isCartPage
                          ? 'font-m-12 max-h-[46px]'
                          : 'font-m-10 max-h-[42px]'
                      }
                    >
                      Size:
                    </b>{' '}
                    <span className="uppercase">{apparelSize}</span>
                  </p>
                )}
              </>
            )}
          </div>
          {isCartPage && !isGwp && (
            <span className="hidden laptop:basis-[15%] laptop:flex laptop:justify-center laptop:font-m-12 laptop:mt-2.5">
              {formatMoney(linePrice)}
              {lineCompareAtPrice &&
                isOnSale(linePrice, lineCompareAtPrice) && (
                  <del class=" text-secondary-1 pl-0.5">
                    {formatMoney(lineCompareAtPrice)}
                  </del>
                )}
            </span>
          )}

          <div
            className={clsx('flex items-center mt-2.5 laptop:mt-0', {
              'laptop:basis-[39%] laptop:items-start laptop:mt-0 laptop:justify-center laptop:pl-[3%]':
                isCartPage,
              '!justify-start': isGwp && !isCartPage,
              '!justify-end': isGwp && isCartPage,
            })}
          >
            {!isGwp && !isGwpLink && !isEnvelope && !isSheath && (
              <div
                className={clsx('flex border border-grey-400 font-r-18', {
                  'laptop:px-2': !isCartPage,
                })}
              >
                <button
                  className={clsx('w-8', {
                    'laptop:w-9': !isCartPage,
                    hidden: isService,
                  })}
                  onClick={() => {
                    adjustQuantity(lineQuantity - 1);
                    analytics.removeToCartTrigger(line);
                  }}
                  ariaLabel={window.TMW.Strings.cart.remove}
                  disabled={isService}
                >
                  &#8722;
                </button>
                <input
                  type="text"
                  value={lineQuantity}
                  onBlur={(e) => adjustQuantity(parseInt(e.target.value, 10))}
                  className="text-center w-10 border-0 heading-6 text-[12px] laptop:py-1.5 laptop:px-1"
                  disabled={isService}
                />
                <button
                  className={clsx('w-8', {
                    hidden: isService,
                  })}
                  onClick={() => adjustQuantity(lineQuantity + 1)}
                  ariaLabel={window.TMW.Strings.cart.add}
                  disabled={isService}
                >
                  &#43;
                </button>
              </div>
            )}
            {!isEnvelope && !isSheath && (
              <button onClick={handleRemove}>
                <TrashIcon
                  className={clsx('w-[22px] ml-3', {
                    'laptop:mt-1.5': isCartPage,
                  })}
                />
              </button>
            )}
          </div>

          {isCartPage && !isGwp && (
            <div className="hidden laptop:flex laptop:basis-[12%] laptop:justify-center laptop:font-m-12 laptop:mt-2.5 laptop:pr-2.5">
              {formatMoney(summ)}
            </div>
          )}

          {!isGwp && isGwpLink && (
            <div className="mt-1 font-m-12 text-primary-1">
              {formatMoney({ amount: gwpLinkRemain })}{' '}
              {window.TMW.Strings.cart.gwpRemainMessage}
            </div>
          )}
        </div>
      </div>
    ),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
      line,
      adjustQuantity,
      cart,
      currentVariant.node.priceV2,
      lineDescription,
      lineImage,
      isCartPage,
      lineQuantity,
    ],
  );

  return lineItem;
};

export default withCartContext(CartItem);
